import React from "react";
import {
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  List,
  ListItem,
  ListItemText,
} from "@material-ui/core";
import { Fullscreen, FullscreenExit, ArrowForward, DragIndicator, TouchApp } from "@material-ui/icons";
import type { DragEndEvent } from "@dnd-kit/core";
import { SortableList } from "shared/SortableList";
import { SortableRow } from "./editorDnd";
import { useHistory } from "react-router-dom";
import {
  useQuestionGroup,
  useQuestions,
  useQuestionGroupUsage,
  useUpdateQuestionGroup,
  useDeleteQuestionGroup,
  useCreateQuestionGroup,
} from "../../queries";
import { isPredicateValidationError, SuggestedDependency } from "../../api";
import { QuestionRef, primaryText, emptyLocalizedText } from "../../types/common";
import { QuestionGroup, expandedQuestionGroupToPersisted } from "../../types/persisted";
import { TranslatedTextField } from "shared/localizedText";
import {
  reorderArray,
  getResponseOptionTexts,
  scrollAndFlash,
  isValidInternalName,
  normalizeInternalName,
  structurallyEqual,
  errorMessage,
  INTERNAL_NAME_HINT,
} from "./editorUtils";
import { cx } from "@emotion/css";
import { editorStyles as styles } from "./editorStyles";
import { PredicateField } from "./feelResolver";
import { useAiLauncher } from "../../ai/AiLauncherContext";
import { AiLaunchButton } from "../../ai/AiLaunchButton";

import type { PickType, PickedItem } from "../../uiTypes";

const QuestionGroupEditor: React.VFC<{
  id: number;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onRequestPick: (pickType: PickType) => void;
  pickedItem: PickedItem;
  onPickConsumed: () => void;
  isPicking: boolean;
  highlight?: string | null;
  onHighlightConsumed?: () => void;
  onDeleted?: () => void;
  onUnsavedChange?: (id: number, dirty: boolean) => void;
}> = ({
  id,
  isExpanded,
  onToggleExpand,
  onRequestPick,
  pickedItem,
  onPickConsumed,
  isPicking,
  highlight,
  onHighlightConsumed,
  onDeleted,
  onUnsavedChange,
}) => {
  const history = useHistory();
  const { data: group, isLoading, error } = useQuestionGroup(id);
  const questionsQuery = useQuestions();
  const { data: usages, isLoading: isLoadingUsages } = useQuestionGroupUsage(id);
  const updateGroup = useUpdateQuestionGroup();
  const deleteGroup = useDeleteQuestionGroup();
  const createGroup = useCreateQuestionGroup();
  const [editedGroup, setEditedGroup] = React.useState<QuestionGroup | null>(null);
  const [expandedVisibility, setExpandedVisibility] = React.useState<string | null>(null);
  const [expandedRequirement, setExpandedRequirement] = React.useState<string | null>(null);
  const { launch } = useAiLauncher();
  const [editingPredicate, setEditingPredicate] = React.useState<string | null>(null);
  const [pickTarget, setPickTargetState] = React.useState<number | null>(null);
  const pickTargetRef = React.useRef<number | null>(null);
  const setPickTarget = React.useCallback((target: number | null) => {
    pickTargetRef.current = target;
    setPickTargetState(target);
  }, []);
  const [pickingDependency, setPickingDependencyState] = React.useState(false);
  const pickingDependencyRef = React.useRef(false);
  const setPickingDependency = React.useCallback((value: boolean) => {
    pickingDependencyRef.current = value;
    setPickingDependencyState(value);
  }, []);
  const [scrollToLast, setScrollToLast] = React.useState(0);
  const lastQuestionRef = React.useRef<HTMLDivElement>(null);
  const savedGroup = React.useRef<QuestionGroup | null>(null);
  const editedGroupRef = React.useRef<QuestionGroup | null>(null);
  editedGroupRef.current = editedGroup;
  const qKeyCounter = React.useRef(0);
  const qKeysRef = React.useRef<string[]>([]);
  const questionRowKey = (idx: number): string => qKeysRef.current[idx] ?? `question-${idx}`;
  const [suggestedDeps, setSuggestedDeps] = React.useState<SuggestedDependency[] | null>(null);

  const feelIdToText = React.useMemo(() => {
    if (!group) return {};
    const map: Record<string, string> = {};
    for (const qRef of group.questions) {
      if (qRef.question?.feel_id && qRef.question.question) {
        map[qRef.question.feel_id] = primaryText(qRef.question.question);
      }
    }
    for (const dep of group.dependencies ?? []) {
      if (dep.feel_id && dep.question) {
        map[dep.feel_id] = primaryText(dep.question);
      }
    }
    return map;
  }, [group]);

  const optionFeelIdToText = React.useMemo(() => {
    if (!group) return {};
    const map: Record<string, string> = {};
    for (const qRef of group.questions) {
      if (qRef.question?.response_type) {
        Object.assign(map, getResponseOptionTexts(qRef.question.response_type));
      }
    }
    for (const dep of group.dependencies ?? []) {
      if (dep.response_type) {
        Object.assign(map, getResponseOptionTexts(dep.response_type));
      }
    }
    return map;
  }, [group]);

  const predicateErrors = React.useMemo(() => {
    const err = updateGroup.error;
    if (!isPredicateValidationError(err)) return {};
    const map: Record<string, string> = {};
    for (const d of err.details) {
      map[d.field] = d.message;
    }
    return map;
  }, [updateGroup.error]);

  React.useEffect(() => {
    if (!group) return;
    const current = editedGroupRef.current;
    const saved = savedGroup.current;
    if (current && saved && !structurallyEqual(current, saved)) return;
    const fromServer = expandedQuestionGroupToPersisted(group);
    setEditedGroup(fromServer);
    savedGroup.current = fromServer;
    qKeysRef.current = fromServer.questions.map(() => `qk-${qKeyCounter.current++}`);
  }, [group]);

  React.useEffect(() => {
    const err = updateGroup.error;
    if (!isPredicateValidationError(err) || !err.details.length) return;
    const first = err.details[0];
    const match = first.field.match(/^questions\[(\d+)\]\.(visibility|requirement)$/);
    if (!match) return;
    const [, idxStr, kind] = match;
    const idx = Number(idxStr);
    const key = questionRowKey(idx);
    if (kind === "visibility") setExpandedVisibility(key);
    else setExpandedRequirement(key);
  }, [updateGroup.error]);

  const hasUnsavedChanges = !!(
    editedGroup &&
    savedGroup.current &&
    !structurallyEqual(editedGroup, savedGroup.current)
  );

  React.useEffect(() => {
    onUnsavedChange?.(id, hasUnsavedChanges);
  }, [id, hasUnsavedChanges, onUnsavedChange]);
  React.useEffect(() => () => onUnsavedChange?.(id, false), [id, onUnsavedChange]);

  const updateQuestion = React.useCallback((idx: number, questionId: number) => {
    setEditedGroup(prev => {
      if (!prev) return prev;
      const newQuestions = [...prev.questions];
      newQuestions[idx] = { ...newQuestions[idx], question_id: questionId };
      return { ...prev, questions: newQuestions };
    });
  }, []);

  React.useEffect(() => {
    if (!pickedItem) return;
    if (pickingDependencyRef.current && pickedItem.pickType === "question") {
      const questionId = pickedItem.id;
      setEditedGroup(prev => {
        if (!prev) return prev;
        const alreadyMember = prev.questions.some(q => q.question_id === questionId);
        const alreadyDep = (prev.dependencies ?? []).some(d => d.question_id === questionId);
        if (alreadyMember || alreadyDep) return prev;
        return { ...prev, dependencies: [...(prev.dependencies ?? []), { question_id: questionId }] };
      });
      setPickingDependency(false);
      onPickConsumed();
      return;
    }
    const target = pickTargetRef.current;
    if (target === null) return;
    if (pickedItem.pickType === "question") {
      updateQuestion(target, pickedItem.id);
    }
    setPickTarget(null);
    onPickConsumed();
  }, [pickedItem, updateQuestion, setPickTarget, setPickingDependency, onPickConsumed]);

  React.useEffect(() => {
    if (!isPicking) {
      setPickTarget(null);
      setPickingDependency(false);
    }
  }, [isPicking, setPickTarget, setPickingDependency]);

  React.useEffect(() => {
    if (scrollToLast > 0 && lastQuestionRef.current) {
      lastQuestionRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [scrollToLast]);

  React.useEffect(() => {
    if (!highlight || !editedGroup) return;
    const [type, idStr] = highlight.split(":");
    const targetId = parseInt(idStr, 10);
    if (type !== "q" || isNaN(targetId)) return;

    scrollAndFlash(`qg-qref-${targetId}`, onHighlightConsumed);
  }, [highlight, editedGroup, onHighlightConsumed]);

  if (isLoading) return <CircularProgress />;
  if (error) return <Typography style={{ color: "#f44336" }}>Failed to load question group</Typography>;
  if (!editedGroup) return null;

  const doSave = (groupToSave: QuestionGroup) => {
    updateGroup.mutate(
      { id, group: groupToSave },
      {
        onSuccess: () => {
          savedGroup.current = groupToSave;
        },
        onError: err => {
          if (isPredicateValidationError(err) && err.suggested_dependencies.length > 0) {
            setSuggestedDeps(err.suggested_dependencies);
          }
        },
      }
    );
  };

  const handleSave = () => {
    const name = editedGroup.name ?? "";
    if (name && !isValidInternalName(name)) {
      const normalized = normalizeInternalName(name);
      if (!window.confirm(`"${name}" is not a valid internal name. Save as "${normalized}"?`)) return;
      const updated = { ...editedGroup, name: normalized };
      setEditedGroup(updated);
      doSave(updated);
      return;
    }
    doSave(editedGroup);
  };

  const handleAcceptSuggestedDeps = () => {
    if (!suggestedDeps || !editedGroup) return;
    const existingIds = new Set([
      ...editedGroup.questions.map(q => q.question_id),
      ...(editedGroup.dependencies ?? []).map(d => d.question_id),
    ]);
    const newDeps = suggestedDeps.filter(s => !existingIds.has(s.question_id));
    const updated = {
      ...editedGroup,
      dependencies: [...(editedGroup.dependencies ?? []), ...newDeps.map(s => ({ question_id: s.question_id }))],
    };
    setEditedGroup(updated);
    setSuggestedDeps(null);
    doSave(updated);
  };

  const onDragEndQuestions = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = qKeysRef.current.indexOf(String(active.id));
    const to = qKeysRef.current.indexOf(String(over.id));
    if (from === -1 || to === -1) return;
    qKeysRef.current = reorderArray(qKeysRef.current, from, to);
    setEditedGroup(prev => {
      if (!prev) return prev;
      return { ...prev, questions: reorderArray(prev.questions, from, to) };
    });
  };

  const removeQuestion = (idx: number) => {
    qKeysRef.current = qKeysRef.current.filter((_, i) => i !== idx);
    setEditedGroup(prev => {
      if (!prev) return prev;
      return { ...prev, questions: prev.questions.filter((_, i) => i !== idx) };
    });
  };

  const setVisibility = (idx: number, type: "visible" | "visibleWhen") => {
    setEditedGroup(prev => {
      if (!prev) return prev;
      const newQuestions = [...prev.questions];
      newQuestions[idx] = {
        ...newQuestions[idx],
        visibility: type === "visible" ? "visible" : { visibleWhen: "" },
      };
      return { ...prev, questions: newQuestions };
    });
  };

  const setVisibilityPredicate = (idx: number, predicate: string) => {
    setEditedGroup(prev => {
      if (!prev) return prev;
      const newQuestions = [...prev.questions];
      newQuestions[idx] = {
        ...newQuestions[idx],
        visibility: { visibleWhen: predicate },
      };
      return { ...prev, questions: newQuestions };
    });
  };

  const setRequirement = (idx: number, type: "optional" | "required" | "requiredWhen") => {
    setEditedGroup(prev => {
      if (!prev) return prev;
      const newQuestions = [...prev.questions];
      newQuestions[idx] = {
        ...newQuestions[idx],
        requirement: type === "requiredWhen" ? { requiredWhen: "" } : type,
      };
      return { ...prev, questions: newQuestions };
    });
  };

  const setRequirementPredicate = (idx: number, predicate: string) => {
    setEditedGroup(prev => {
      if (!prev) return prev;
      const newQuestions = [...prev.questions];
      newQuestions[idx] = {
        ...newQuestions[idx],
        requirement: { requiredWhen: predicate },
      };
      return { ...prev, questions: newQuestions };
    });
  };

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete this question group?`)) {
      deleteGroup.mutate(id, {
        onSuccess: () => onDeleted?.(),
      });
    }
  };

  const handleDuplicate = () => {
    if (!group) return;
    const persisted = expandedQuestionGroupToPersisted(group);
    const base = persisted.name && isValidInternalName(persisted.name) ? persisted.name : "untitled";
    const name = `${base}_copy_${Date.now()}`;
    createGroup.mutate(
      {
        name,
        title: persisted.title,
        description: persisted.description,
        questions: persisted.questions,
        dependencies: persisted.dependencies,
      },
      { onSuccess: data => history.push(`/health-forms/question-groups/${data.id}`) }
    );
  };

  return (
    <div className={styles.editorContainer} style={isPicking ? { pointerEvents: "none" } : undefined}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        px={2}
        style={isPicking ? { opacity: 0.3 } : undefined}
      >
        <span />
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <AiLaunchButton
            context={{ kind: "questionGroup", id, label: editedGroup?.name || `Question group #${id}` }}
            tooltip="Edit this question group with AI"
          />
          <Tooltip title={isExpanded ? "Minimize" : "Expand"}>
            <IconButton size="small" onClick={onToggleExpand}>
              {isExpanded ? <FullscreenExit /> : <Fullscreen />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <div className={cx(styles.form, isPicking && styles.formPicking)}>
        <Box className={styles.toolbar} display="flex" justifyContent="flex-end" alignItems="center" style={{ gap: 8 }}>
          <Button
            size="small"
            onClick={() => {
              qKeysRef.current.push(`qk-${qKeyCounter.current++}`);
              setEditedGroup(prev => {
                if (!prev) return prev;
                const newRef: QuestionRef = { question_id: 0, visibility: "visible", requirement: "optional" };
                return { ...prev, questions: [...prev.questions, newRef] };
              });
              setScrollToLast(c => c + 1);
            }}
          >
            Add Question
          </Button>
          <Button size="small" style={{ color: "#f44336" }} onClick={handleDelete} disabled={deleteGroup.isLoading}>
            {deleteGroup.isLoading ? "Deleting..." : "Delete"}
          </Button>
          <Button size="small" onClick={handleDuplicate} disabled={createGroup.isLoading}>
            {createGroup.isLoading ? "Duplicating..." : "Duplicate"}
          </Button>
          {hasUnsavedChanges && (
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={() => {
                if (savedGroup.current) {
                  setEditedGroup(savedGroup.current);
                  qKeysRef.current = savedGroup.current.questions.map(() => `qk-${qKeyCounter.current++}`);
                }
              }}
            >
              Revert
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleSave}
            disabled={updateGroup.isLoading}
          >
            {updateGroup.isLoading ? "Saving..." : hasUnsavedChanges ? "Save*" : "Save"}
          </Button>
        </Box>

        <TextField
          label="Name"
          value={editedGroup.name ?? ""}
          onChange={e => setEditedGroup(prev => (prev ? { ...prev, name: e.target.value } : prev))}
          fullWidth
          variant="outlined"
          margin="normal"
          error={!isValidInternalName(editedGroup.name)}
          helperText={!isValidInternalName(editedGroup.name) ? INTERNAL_NAME_HINT : undefined}
        />

        <TranslatedTextField
          label="Title"
          value={editedGroup.title ?? emptyLocalizedText()}
          onChange={value =>
            setEditedGroup(prev => (prev ? { ...prev, title: primaryText(value) ? value : undefined } : prev))
          }
        />

        <TranslatedTextField
          label="Description"
          value={editedGroup.description ?? emptyLocalizedText()}
          onChange={value =>
            setEditedGroup(prev => (prev ? { ...prev, description: primaryText(value) ? value : undefined } : prev))
          }
          multiline
          rows={2}
        />

        <Box mt={3} mb={2}>
          <Typography variant="subtitle2">Questions ({editedGroup.questions.length})</Typography>
        </Box>

        <SortableList items={qKeysRef.current} onDragEnd={onDragEndQuestions}>
          {editedGroup.questions.map((qRef, idx) => {
            const isVisibilityConditional = typeof qRef.visibility === "object";
            const isRequirementConditional = typeof qRef.requirement === "object";
            const isPickTarget = pickTarget === idx;
            const rowKey = questionRowKey(idx);

            return (
              <SortableRow
                key={rowKey}
                id={rowKey}
                domId={`qg-qref-${qRef.question_id}`}
                className={styles.questionRefCard}
                extraStyle={isPickTarget ? { background: "#fff3cd", border: "2px solid #ffc107" } : undefined}
              >
                {handleProps => (
                  <>
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                      <span {...handleProps} style={{ cursor: "grab", display: "flex" }}>
                        <DragIndicator fontSize="small" style={{ color: "#888" }} />
                      </span>

                      <Box display="flex" flexDirection="column" style={{ flex: 1, gap: 4, minWidth: 0 }}>
                        <Box display="flex" alignItems="center" style={{ gap: 8, width: "100%" }}>
                          <Typography
                            variant="body2"
                            noWrap
                            style={{ flex: 1, minWidth: 0, color: qRef.question_id ? undefined : "#999" }}
                          >
                            {(() => {
                              const q = questionsQuery.data?.find(q => q.id === qRef.question_id);
                              return q ? q.question || `Question #${q.id}` : "-- Select a question --";
                            })()}
                          </Typography>
                          <Tooltip title="Pick question">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setPickTarget(idx);
                                onRequestPick("question");
                              }}
                              style={{ padding: 4, flexShrink: 0 }}
                            >
                              <TouchApp fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {!!qRef.question_id && (
                            <Tooltip title="Open question">
                              <IconButton
                                size="small"
                                onClick={() => history.push(`/health-forms/questions/${qRef.question_id}`)}
                                style={{ padding: 4, flexShrink: 0 }}
                              >
                                <ArrowForward fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                        {isPickTarget && (
                          <Typography variant="caption" style={{ color: "#856404" }}>
                            ← Select from sidebar
                          </Typography>
                        )}
                      </Box>

                      <Button
                        size="small"
                        onClick={() => setExpandedVisibility(expandedVisibility === rowKey ? null : rowKey)}
                        style={{
                          background: isVisibilityConditional ? "#ffc107" : "#28a745",
                          color: isVisibilityConditional ? "#000" : "#fff",
                        }}
                      >
                        👁️
                      </Button>

                      <Button
                        size="small"
                        onClick={() => setExpandedRequirement(expandedRequirement === rowKey ? null : rowKey)}
                        style={{
                          background: isRequirementConditional
                            ? "#ffc107"
                            : qRef.requirement === "required"
                              ? "#1976d2"
                              : "#6c757d",
                          color: isRequirementConditional ? "#000" : "#fff",
                        }}
                      >
                        ⚙️
                      </Button>

                      <Button size="small" style={{ color: "#f44336" }} onClick={() => removeQuestion(idx)}>
                        ✕
                      </Button>
                    </Box>

                    {expandedVisibility === rowKey && (
                      <Box className={styles.expandedSection}>
                        <Typography variant="caption" gutterBottom display="block">
                          Visibility
                        </Typography>
                        <Box display="flex" style={{ gap: 8 }} mb={1}>
                          <Button
                            size="small"
                            variant={qRef.visibility === "visible" ? "contained" : "outlined"}
                            onClick={() => setVisibility(idx, "visible")}
                          >
                            Visible
                          </Button>
                          <Button
                            size="small"
                            variant={isVisibilityConditional ? "contained" : "outlined"}
                            onClick={() => setVisibility(idx, "visibleWhen")}
                          >
                            Visible When
                          </Button>
                        </Box>
                        {typeof qRef.visibility === "object" && (
                          <PredicateField
                            predicate={qRef.visibility.visibleWhen}
                            feelIdToText={feelIdToText}
                            optionFeelIdToText={optionFeelIdToText}
                            isEditing={editingPredicate === `visibility-${rowKey}`}
                            onStartEditing={() => setEditingPredicate(`visibility-${rowKey}`)}
                            onStopEditing={() => setEditingPredicate(null)}
                            onChange={v => setVisibilityPredicate(idx, v)}
                            error={predicateErrors[`questions[${idx}].visibility`]}
                            onAiRequest={() => {
                              const name = questionsQuery.data?.find(q => q.id === qRef.question_id)?.question;
                              launch({
                                kind: "predicate",
                                scope: "questionGroup",
                                scopeId: id,
                                scopeLabel: editedGroup?.name || `Question group #${id}`,
                                predicateKind: "visibility",
                                location: `question ${idx + 1}`,
                                targetKind: "question",
                                targetId: qRef.question_id,
                                targetLabel: name ?? "unknown",
                              });
                            }}
                          />
                        )}
                      </Box>
                    )}

                    {expandedRequirement === rowKey && (
                      <Box className={styles.expandedSectionAlt}>
                        <Typography variant="caption" gutterBottom display="block">
                          Requirement
                        </Typography>
                        <Box display="flex" style={{ gap: 8 }} mb={1}>
                          <Button
                            size="small"
                            variant={qRef.requirement === "optional" ? "contained" : "outlined"}
                            onClick={() => setRequirement(idx, "optional")}
                          >
                            Optional
                          </Button>
                          <Button
                            size="small"
                            variant={qRef.requirement === "required" ? "contained" : "outlined"}
                            onClick={() => setRequirement(idx, "required")}
                          >
                            Required
                          </Button>
                          <Button
                            size="small"
                            variant={isRequirementConditional ? "contained" : "outlined"}
                            onClick={() => setRequirement(idx, "requiredWhen")}
                          >
                            Required When
                          </Button>
                        </Box>
                        {typeof qRef.requirement === "object" && (
                          <PredicateField
                            predicate={qRef.requirement.requiredWhen}
                            feelIdToText={feelIdToText}
                            optionFeelIdToText={optionFeelIdToText}
                            isEditing={editingPredicate === `requirement-${rowKey}`}
                            onStartEditing={() => setEditingPredicate(`requirement-${rowKey}`)}
                            onStopEditing={() => setEditingPredicate(null)}
                            onChange={v => setRequirementPredicate(idx, v)}
                            error={predicateErrors[`questions[${idx}].requirement`]}
                            onAiRequest={() => {
                              const name = questionsQuery.data?.find(q => q.id === qRef.question_id)?.question;
                              launch({
                                kind: "predicate",
                                scope: "questionGroup",
                                scopeId: id,
                                scopeLabel: editedGroup?.name || `Question group #${id}`,
                                predicateKind: "requirement",
                                location: `question ${idx + 1}`,
                                targetKind: "question",
                                targetId: qRef.question_id,
                                targetLabel: name ?? "unknown",
                              });
                            }}
                          />
                        )}
                      </Box>
                    )}
                  </>
                )}
              </SortableRow>
            );
          })}
          <div ref={lastQuestionRef} />
        </SortableList>

        <Box mt={3} mb={2}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2">Dependencies ({(editedGroup.dependencies ?? []).length})</Typography>
            <Button
              size="small"
              onClick={() => {
                setPickingDependency(true);
                onRequestPick("question");
              }}
            >
              Add Dependency
            </Button>
          </Box>
          {pickingDependency && (
            <Typography variant="caption" style={{ color: "#856404" }}>
              ← Select a question from sidebar
            </Typography>
          )}
          {(editedGroup.dependencies ?? []).length === 0 && !pickingDependency && (
            <Typography variant="body2" color="textSecondary" style={{ fontStyle: "italic", marginTop: 4 }}>
              No external dependencies
            </Typography>
          )}
          {(editedGroup.dependencies ?? []).map(dep => {
            const q = questionsQuery.data?.find(q => q.id === dep.question_id);
            const isMemberConflict = editedGroup.questions.some(qr => qr.question_id === dep.question_id);
            return (
              <Box
                key={dep.question_id}
                display="flex"
                alignItems="center"
                style={{
                  gap: 8,
                  marginTop: 4,
                  padding: "4px 8px",
                  background: isMemberConflict ? "#fff0f0" : "#f5f5f5",
                  borderRadius: 4,
                  border: isMemberConflict ? "1px solid #f44336" : undefined,
                }}
              >
                <Typography variant="body2" style={{ flex: 1, minWidth: 0 }} noWrap>
                  {q ? q.question || `Question #${q.id}` : `Question #${dep.question_id}`}
                </Typography>
                {isMemberConflict && (
                  <Typography variant="caption" style={{ color: "#f44336", flexShrink: 0 }}>
                    Also a group member
                  </Typography>
                )}
                <IconButton
                  size="small"
                  onClick={() => history.push(`/health-forms/questions/${dep.question_id}`)}
                  style={{ padding: 4, flexShrink: 0 }}
                >
                  <ArrowForward fontSize="small" />
                </IconButton>
                <Button
                  size="small"
                  style={{ color: "#f44336", minWidth: 0, padding: "2px 4px" }}
                  onClick={() => {
                    setEditedGroup(prev => {
                      if (!prev) return prev;
                      return {
                        ...prev,
                        dependencies: (prev.dependencies ?? []).filter(d => d.question_id !== dep.question_id),
                      };
                    });
                  }}
                >
                  ✕
                </Button>
              </Box>
            );
          })}
        </Box>

        <Box mt={2}>
          <Typography variant="caption" color="textSecondary" display="block">
            ID: {editedGroup.id}
          </Typography>
          {editedGroup.updated_at && (
            <Typography variant="caption" color="textSecondary" display="block">
              Updated: {new Date(editedGroup.updated_at).toLocaleString()}
            </Typography>
          )}
          {editedGroup.edited_by && (
            <Typography variant="caption" color="textSecondary" display="block">
              Edited by: {editedGroup.edited_by}
            </Typography>
          )}
        </Box>

        <Divider style={{ marginTop: 24, marginBottom: 16 }} />

        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Used in
          </Typography>
          {isLoadingUsages ? (
            <CircularProgress size={20} />
          ) : !usages?.length ? (
            <Typography variant="body2" color="textSecondary" style={{ fontStyle: "italic" }}>
              Not used in any questionnaires
            </Typography>
          ) : (
            <Box display="flex" flexDirection="column" style={{ gap: 8 }}>
              {usages.map(usage => (
                <Box
                  key={`${usage.type}-${usage.id}`}
                  className={styles.usageItem}
                  onClick={() => {
                    const params = new URLSearchParams();
                    params.set("highlight", `qg:${id}`);
                    if (usage.page_position) params.set("page", String(usage.page_position));
                    history.push(`/health-forms/questionnaires/${usage.id}?${params}#part-qg-${id}`);
                  }}
                >
                  <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    <span style={{ color: "#28a745", fontSize: 16 }}>●</span>
                    <Typography variant="body2">{usage.name}</Typography>
                  </Box>
                  <Typography variant="caption" color="textSecondary">
                    {usage.id}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {updateGroup.isError && !isPredicateValidationError(updateGroup.error) && (
          <Typography color="error" variant="caption" display="block" style={{ marginTop: 8 }}>
            Failed to save: {errorMessage(updateGroup.error)}
          </Typography>
        )}
        {updateGroup.isError && isPredicateValidationError(updateGroup.error) && (
          <Typography color="error" variant="caption" display="block" style={{ marginTop: 8 }}>
            Failed to save: {updateGroup.error.details.length} predicate error(s) — see highlighted fields above
          </Typography>
        )}
        {updateGroup.isSuccess && (
          <Typography color="primary" variant="caption" display="block" style={{ marginTop: 8 }}>
            Saved successfully
          </Typography>
        )}
      </div>

      <Dialog open={!!suggestedDeps} onClose={() => setSuggestedDeps(null)}>
        <DialogTitle>Add missing dependencies?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            These predicates reference questions not in this group. Add them as dependencies?
          </DialogContentText>
          <List dense>
            {suggestedDeps?.map(s => {
              const q = questionsQuery.data?.find(q => q.id === s.question_id);
              return (
                <ListItem key={s.question_id}>
                  <ListItemText primary={q?.question || `Question #${s.question_id}`} secondary={s.feel_id} />
                </ListItem>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuggestedDeps(null)}>Cancel</Button>
          <Button onClick={handleAcceptSuggestedDeps} color="primary" variant="contained">
            Add & Save
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default QuestionGroupEditor;
